/* MIMOSA infinite scroll for collection pages (v2, 2026-07-12)
 * Auto-loads the next page of products when the shopper nears the bottom.
 * - Hides numbered pagination when JS is available (falls back to it without JS)
 * - Re-runs the theme's lazy image loader (initLazyImages) on every appended batch
 * - Survives AJAX filter/sort re-renders
 * - Dedupes products and stops cleanly on the last page
 * - BACK-BUTTON RESTORE: when a shopper opens a product and navigates back,
 *   all previously auto-loaded pages are re-loaded and their scroll position
 *   is restored, so they continue browsing exactly where they left off.
 */
(function () {
  'use strict';

  var STORE_KEY = 'mimosa-inf-scroll';
  var RESTORE_MAX_AGE_MS = 30 * 60 * 1000; /* 30 minutes */

  var state = {
    nextUrl: null,
    loading: false,
    sentinel: null,
    observer: null,
    seen: {},
    knownGrid: null,
    pagesLoaded: 0
  };

  function pageKey() {
    return location.pathname + location.search;
  }

  function getResults(root) {
    return (root || document).querySelector('#filter-results');
  }

  function getGrid(root) {
    var r = getResults(root);
    return r ? r.querySelector('ul.grid') : null;
  }

  function getNextUrl(root) {
    var r = getResults(root);
    if (!r) return null;
    var a = r.querySelector('.pagination__arrow--next[href]');
    return a ? a.getAttribute('href') : null;
  }

  function hidePagination(root) {
    var r = getResults(root);
    if (!r) return;
    var pag = r.querySelector('.pagination');
    if (pag) {
      var nav = pag.closest('nav') || pag;
      nav.style.display = 'none';
    }
  }

  function removeSentinel() {
    if (state.sentinel) {
      if (state.observer) state.observer.unobserve(state.sentinel);
      if (state.sentinel.parentNode) state.sentinel.parentNode.removeChild(state.sentinel);
      state.sentinel = null;
    }
  }

  function ensureSentinel() {
    var r = getResults();
    if (!r || !state.nextUrl) return;
    if (state.sentinel && r.contains(state.sentinel)) return;
    removeSentinel();
    var s = document.createElement('div');
    s.className = 'js-infinite-sentinel';
    s.style.cssText = 'width:100%;min-height:2px;text-align:center;padding:24px 0;';
    s.setAttribute('aria-hidden', 'true');
    r.appendChild(s);
    state.sentinel = s;
    if (state.observer) state.observer.observe(s);
  }

  function seedSeen() {
    state.seen = {};
    var g = getGrid();
    if (!g) return;
    g.querySelectorAll('.js-prod-link').forEach(function (a) {
      var href = a.getAttribute('href');
      if (href) state.seen[href.split('?')[0]] = true;
    });
  }

  function runLazyImages() {
    if (typeof window.initLazyImages === 'function') {
      window.initLazyImages();
    } else if (typeof initLazyImages === 'function') {
      initLazyImages();
    }
  }

  /**
   * Loads the next page of products and appends them to the grid.
   * @param {Function} [done] - Optional callback fired when the batch finishes (success or failure).
   */
  function loadNext(done) {
    if (state.loading || !state.nextUrl) {
      if (done) done(false);
      return;
    }
    state.loading = true;
    if (state.sentinel) state.sentinel.textContent = 'Loading more\u2026';

    fetch(state.nextUrl, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = getGrid(doc);
        var curGrid = getGrid();

        if (newGrid && curGrid) {
          newGrid.querySelectorAll(':scope > li').forEach(function (li) {
            var link = li.querySelector('.js-prod-link');
            var key = link ? (link.getAttribute('href') || '').split('?')[0] : null;
            if (key) {
              if (state.seen[key]) return;
              state.seen[key] = true;
            }
            curGrid.appendChild(document.importNode(li, true));
          });

          /* Re-run the theme's lazy image loader so appended cards get real images. */
          runLazyImages();
        }

        state.nextUrl = getNextUrl(doc);
        state.pagesLoaded += 1;
        state.loading = false;

        if (state.sentinel) state.sentinel.textContent = '';

        if (state.nextUrl) {
          ensureSentinel();
        } else {
          removeSentinel();
        }

        if (done) done(true);
      })
      .catch(function () {
        state.loading = false;
        if (state.sentinel) state.sentinel.textContent = '';
        if (done) done(false);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Back-button position restore                                        */
  /* ------------------------------------------------------------------ */

  function saveState() {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        url: pageKey(),
        pages: state.pagesLoaded,
        scrollY: window.pageYOffset || 0,
        t: Date.now()
      }));
    } catch (e) { /* storage unavailable - degrade gracefully */ }
  }

  function clearSavedState() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
  }

  function readSavedState() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || saved.url !== pageKey()) return null;
      if (Date.now() - (saved.t || 0) > RESTORE_MAX_AGE_MS) return null;
      return saved;
    } catch (e) {
      return null;
    }
  }

  /* Save position whenever a product card link (or quick-view button) is clicked. */
  function bindClickCapture() {
    document.addEventListener('click', function (evt) {
      if (!getResults()) return;
      var a = evt.target.closest ? evt.target.closest('.js-prod-link, .quick-add-view-btn') : null;
      if (a) saveState();
    }, true);
  }

  /* On return: re-load the same number of pages, then jump back to the spot. */
  function restoreIfNeeded() {
    var saved = readSavedState();
    if (!saved) return;
    clearSavedState();

    var remaining = saved.pages || 0;

    function finish() {
      if (saved.scrollY > 0) {
        requestAnimationFrame(function () {
          window.scrollTo(0, saved.scrollY);
          /* second pass after images/labels settle */
          setTimeout(function () { window.scrollTo(0, saved.scrollY); }, 400);
        });
      }
    }

    (function chain() {
      if (remaining <= 0 || !state.nextUrl) {
        finish();
        return;
      }
      remaining -= 1;
      loadNext(function () { chain(); });
    })();
  }

  /* ------------------------------------------------------------------ */

  function init() {
    var r = getResults();
    if (!r) return;

    state.knownGrid = getGrid();
    state.nextUrl = getNextUrl();
    state.pagesLoaded = 0;
    hidePagination();

    if (!state.nextUrl) {
      removeSentinel();
      return;
    }

    seedSeen();

    if (!state.observer && 'IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) loadNext();
        });
      }, { rootMargin: '0px 0px 900px 0px' });
    }

    if (!state.observer && !state.scrollFallbackBound) {
      state.scrollFallbackBound = true;
      window.addEventListener('scroll', function () {
        if ((window.innerHeight + window.pageYOffset) >= (document.body.offsetHeight - 900)) {
          loadNext();
        }
      }, { passive: true });
    }

    ensureSentinel();
  }

  /* Re-initialise when the facet filters / sorting AJAX re-renders the results. */
  function watchRerenders() {
    if (!('MutationObserver' in window)) return;
    var r = getResults();
    if (!r) return;

    var t = null;
    var mo = new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var g = getGrid();
        if (g && g !== state.knownGrid) {
          clearSavedState(); /* filters changed - old position is meaningless */
          init();
        }
      }, 150);
    });

    mo.observe(r, { childList: true, subtree: true });
  }

  function start() {
    if (!getResults()) return; /* not a collection/search results page */
    init();
    bindClickCapture();
    watchRerenders();
    restoreIfNeeded();
  }

  /* If the browser restores the page from back-forward cache, the DOM (and all
     appended products) are already intact - drop the saved state so we don't
     double-restore on a later visit. */
  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) clearSavedState();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
